import { useEffect, useState, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Area, ComposedChart } from "recharts";
import api from "@/lib/api";
import { FiltersBar, Kpi, Spark, Loading, CARD, LBL, fmtAht, fmtNum, fmtDelta, fq, thrColor } from "./axiom";

const MonLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(y, mo - 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

const ChangeList = ({ title, rows, months, inc }) => (
  <div className={`${CARD} p-5`} style={{ borderTop: `3px solid ${inc ? "#F43F5E" : "#10B981"}` }}>
    <p className="mb-4 text-sm font-bold text-thstrong">{inc ? "" : ""}{title}</p>
    {rows.length === 0 && <p className="py-6 text-center text-xs text-thdim">No {inc ? "increases" : "decreases"} with enough data</p>}
    {rows.map((r, i) => (
      <div key={r.name} className="flex items-center justify-between gap-3 border-b border-thborder/6 py-2.5 last:border-0">
        <div className="flex min-w-0 items-center gap-3">
          <span className="font-mono-hud text-xs text-thfaint">{i + 1}</span>
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-thbody">{r.name}</p>
            <p className="font-mono-hud text-[10px] text-thdim">{fmtAht(r.first)} → {fmtAht(r.last)} · {fmtNum(r.calls)} calls</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className={`font-mono-hud text-xs font-bold ${inc ? "text-red-500" : "text-thmint"}`}>{fmtDelta(r.delta)}</span>
          <Spark series={r.series} color={inc ? "#F43F5E" : "#10B981"} />
        </div>
      </div>
    ))}
  </div>
);

const TrendTable = ({ title, rows, months, thr, showCalls }) => (
  <div className={`${CARD} mt-6 overflow-x-auto`}>
    <p className="px-5 pb-2 pt-4 text-sm font-bold text-thstrong">{title}</p>
    <table className="w-full text-left">
      <thead><tr className="border-b border-thborder/8">
        <th className={`${LBL} px-5 py-2`}>Name</th>
        {months.map((m) => <th key={m} className={`${LBL} whitespace-nowrap px-3 py-2`}>{MonLabel(m)}</th>)}
        <th className={`${LBL} px-3 py-2`}>Spark</th>
        <th className={`${LBL} px-3 py-2`}>Δ Total</th>
        {showCalls && <th className={`${LBL} px-3 py-2 text-right`}>Calls</th>}
      </tr></thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.name} className="border-b border-thborder/6">
            <td className="whitespace-nowrap px-5 py-2 text-xs font-semibold text-thbody">{r.name}</td>
            {months.map((m) => (
              <td key={m} className="px-1 py-1">
                <span className="font-mono-hud block rounded px-2 py-1.5 text-center text-xs font-bold" style={thrColor(r.cells[m], thr)}>
                  {fmtAht(r.cells[m])}
                </span>
              </td>
            ))}
            <td className="px-3 py-2"><Spark series={r.series} /></td>
            <td className={`font-mono-hud px-3 py-2 text-xs font-bold ${r.delta > 0 ? "text-red-500" : r.delta < 0 ? "text-thmint" : "text-thdim"}`}>{fmtDelta(r.delta)}</td>
            {showCalls && <td className="font-mono-hud px-3 py-2 text-right text-xs text-thmute">{fmtNum(r.calls)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const TrendsTab = ({ summary, filters, setFilters }) => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/aht/settings").then((r) => setSettings(r.data)); }, []);
  const load = useCallback(() => { api.get(`/aht/trends?${fq(filters)}`).then((r) => setData(r.data)); }, [filters]);
  useEffect(() => { load(); }, [load]);

  if (!data || !settings) return <Loading />;
  const k = data.kpis;
  const single = data.months.length < 2;

  return (
    <div>
      <h2 className="font-heading mb-1 text-xl font-bold text-thstrong">Monthly AHT Trends</h2>
      {data.months.length >= 2 && (
        <p className="font-mono-hud mb-5 text-[11px] text-thdim">
          MoM comparisons: <span className="text-amber-600">{MonLabel(data.months[data.months.length - 1])}</span> vs <span className="text-amber-600">{MonLabel(data.months[data.months.length - 2])}</span>
        </p>
      )}
      <FiltersBar summary={summary} filters={filters} setFilters={setFilters} />

      <div className="mb-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi testId="aht-trend-mom" label="Latest MoM" value={fmtDelta(k.latest_mom)} border="#F59E0B"
          accent={k.latest_mom > 0 ? "#F43F5E" : "#10B981"} sub={single ? "Needs 2+ months" : ""} />
        <Kpi testId="aht-trend-direction" label="3M Direction" value={k.direction ? `${k.direction} ${k.direction === "Worsening" ? "↑" : k.direction === "Improving" ? "↓" : "→"}` : "—"}
          accent={k.direction === "Worsening" ? "#F43F5E" : k.direction === "Improving" ? "#10B981" : "#7a8fb5"} border="#F43F5E" sub="Across last months" />
        <Kpi testId="aht-trend-best-lob" label="Best LOB Trend" value={k.best_lob ? fmtDelta(k.best_lob.delta) : "—"} accent="#10B981" border="#10B981"
          sub={k.best_lob ? `${k.best_lob.name} · ${fmtAht(k.best_lob.latest)} latest` : ""} />
        <Kpi testId="aht-trend-worst-lob" label="Worst LOB Trend" value={k.worst_lob ? fmtDelta(k.worst_lob.delta) : "—"} accent="#F43F5E" border="#F43F5E"
          sub={k.worst_lob ? `${k.worst_lob.name} · ${fmtAht(k.worst_lob.latest)} latest` : ""} />
      </div>

      <div className={`${CARD} mb-6 p-6`}>
        <p className="mb-5 text-sm font-bold text-thstrong">Overall Monthly AHT Trend</p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.overall} margin={{ top: 20, right: 30, left: 10 }}>
              <XAxis dataKey="month" tickFormatter={MonLabel} tick={{ fill: "#7a8fb5", fontSize: 10, fontFamily: "JetBrains Mono" }} />
              <YAxis tick={{ fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(v) => fmtAht(v)} domain={["dataMin - 30", "dataMax + 30"]} />
              <Tooltip formatter={(v, n) => (n === "AHT" ? fmtAht(v) : fmtNum(v))} labelFormatter={MonLabel} contentStyle={{ background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" }} />
              <Area dataKey="aht" name="AHT" fill="rgb(var(--th-amber) / 0.12)" stroke="none" />
              <Line dataKey="aht" name="AHT" stroke="#10B981" strokeWidth={2} dot={{ r: 4, fill: "#fff", stroke: "#F59E0B", strokeWidth: 2 }}>
                <LabelList dataKey="aht" position="top" formatter={(v) => fmtAht(v)} style={{ fontSize: 10, fontWeight: 700, fill: "rgb(var(--th-strong))", fontFamily: "JetBrains Mono" }} />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex gap-6 overflow-x-auto">
          {data.overall.map((o) => (
            <div key={o.month} className="shrink-0 text-center">
              <p className="font-mono-hud text-[10px] text-thdim">{MonLabel(o.month)}</p>
              <p className="font-mono-hud text-[10px] text-thfaint">{fmtNum(o.calls)}</p>
              {o.delta != null && <p className={`font-mono-hud text-[10px] font-bold ${o.delta > 0 ? "text-red-500" : "text-thmint"}`}>{fmtDelta(o.delta)}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChangeList title="Categories — AHT Increasing" rows={data.cat_inc} inc />
        <ChangeList title="Categories — AHT Decreasing" rows={data.cat_dec} />
        <ChangeList title="Sub-Categories — AHT Increasing" rows={data.sub_inc} inc />
        <ChangeList title="Sub-Categories — AHT Decreasing" rows={data.sub_dec} />
      </div>

      <TrendTable title="LOB Monthly Trend" rows={data.lob_table} months={data.months} thr={settings.thresholds.lob} />
      <TrendTable title="Category Monthly Trend" rows={data.cat_table} months={data.months} thr={settings.thresholds.category} showCalls />
    </div>
  );
};
